/**
 * CmsSpaceLayout — Wrapper layout for all CMS space sub-pages.
 *
 * Provides a breadcrumb header while rendering the active child route via <Outlet />.
 * Navigation is handled by SpaceSidebar in the outer Layout.
 * Only used for "list" pages — full-screen editors are routed separately.
 */

import { Link, Outlet, useParams } from 'react-router-dom';
import { getCurrentUser } from '../../lib/auth.ts';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Button } from '../ui/Button.tsx';
import { Skeleton } from '../ui/Skeleton.tsx';

interface CmsSpace {
  id: string;
  name: string;
  slug: string;
  type: string;
  domain: string | null;
}

function getPreviewUrl(space: CmsSpace): string | null {
  if (space.domain) return `https://${space.domain}`;
  const WEBSITE_BASE_DOMAIN = import.meta.env.VITE_WEBSITE_BASE_DOMAIN as string | undefined;
  const tenantSlug = getCurrentUser()?.tenantSlug;
  if (WEBSITE_BASE_DOMAIN && tenantSlug) {
    return `https://${space.slug}--${tenantSlug}.${WEBSITE_BASE_DOMAIN}`;
  }
  return null;
}

export function CmsSpaceLayout() {
  const { spaceId } = useParams<{ spaceId: string }>();

  const { data: space, isLoading } = useQuery<CmsSpace>({
    queryKey: ['cms-space', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}`).then((r) => r.data),
    enabled: !!spaceId,
  });

  if (isLoading || !space) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const previewUrl = getPreviewUrl(space);

  return (
    <div>
      {/* Breadcrumb + header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
            <Link to="/spaces" className="hover:text-zinc-600 flex items-center gap-1">
              <ChevronLeft size={13} />
              All Spaces
            </Link>
          </div>
          <h1 className="text-lg font-semibold text-zinc-900">{space.name}</h1>
          <p className="text-xs text-zinc-400 font-mono mt-0.5">{space.slug}</p>
        </div>
        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="secondary">
              <ExternalLink size={12} className="mr-1" />
              Open website
            </Button>
          </a>
        )}
      </div>

      {/* Child route — full width */}
      <Outlet />
    </div>
  );
}
